import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./styles/rtl.css";
import App from "./App";
import { initializeDirection } from "./utils/rtlUtils";
import "./i18n";

// Initialize RTL direction on app start
initializeDirection();

// ─── React-Snap hydration handshake (Jun 2026) ───────────────────────────────
// react-snap pre-renders the SEO-critical marketing routes (Landing,
// /vs/*, /regions/*, /blog/*) at build time. At runtime the resulting
// HTML must be HYDRATED (so React reuses the existing DOM) instead of
// re-rendered (which would blow away the prerendered markup and cause
// a flash of unstyled content).
//
// Detection rule: if #root already has children, we know react-snap
// (or any other prerender) populated it — switch to hydrateRoot.
// Otherwise (first run on a route that wasn't snapped, dev mode, or
// the app shell on dynamic routes) fall back to createRoot.
const rootElement = document.getElementById("root");
if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, <App />);
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}

// ─── Skip side effects when crawled by react-snap ────────────────────────────
// react-snap sets navigator.userAgent to "ReactSnap". During prerender
// we DON'T want to register the service worker (would serialise into
// the static HTML and confuse the runtime SW registration) or run the
// version-bust loop (would trigger an infinite reload inside puppeteer).
if (navigator.userAgent === 'ReactSnap') {
  // Halt all post-render side effects below. The prerendered HTML is
  // returned cleanly with Helmet's <title>, meta, JSON-LD baked in.
  console.log('react-snap prerender detected — skipping SW + version check');
} else {

// ═══════════════════════════════════════════════════════════════════════
//  PWA AUTO-UPDATE — Feb 2026 rewrite
//
//  Old behaviour:
//   • APP_VERSION constant drifted from version.json / service-worker.js.
//   • On version mismatch we ONLY called registration.update() — that's a
//     soft "please check the network for a new SW" and the WebView could
//     still serve cached old JS chunks for hours.
//   • Cashier had to manually hard-reset the iPad / Android shell after
//     every Vercel deploy or login would silently fail (cached chunk
//     references stale auth code).
//
//  New behaviour:
//   • Single source of truth: APP_VERSION here MUST match version.json.
//   • On a real mismatch, ATOMICALLY: unregister all SWs → delete all
//     caches → set a one-shot "did-bust-for-vX" localStorage flag → hard
//     reload. Flag prevents reload loops.
//   • Tablet / shell users get the new bundle within ~10s of a Vercel
//     deploy with zero manual intervention.
// ═══════════════════════════════════════════════════════════════════════
const APP_VERSION = '2.3.0';
const CACHE_BUST_FLAG_KEY = `ff_cache_busted_for_v${APP_VERSION}`;

// Register Service Worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log(`Service Worker registered (App v${APP_VERSION}):`, registration.scope);

        // Active version check every 60 s — frequent enough that an evening
        // deploy reaches a busy cashier before the next ring. The actual
        // cache-bust only fires when version.json differs.
        setInterval(() => {
          registration.update();
          checkServerVersion();
        }, 60 * 1000);

        // Listen for new service worker installation
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                console.log('New version ready — will apply on next visit');
              } else {
                console.log('App installed for offline use');
              }
            }
          });
        });
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'APP_UPDATED') {
        console.log(`App updated to v${event.data.version}`);
      }
    });
  });
}

// Early-boot version probe (runs once, ~3 s after load to let the page settle)
setTimeout(() => { checkServerVersion(); }, 3000);

// ─── Hard cache-bust on version mismatch ──────────────────────────────
async function bustCachesAndReload(targetVersion) {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister().catch(() => {})));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
    }
  } catch (e) {
    console.warn('Cache bust step failed (non-fatal):', e);
  }
  // Mark this version as "applied" so we don't loop. Two-tier flag —
  // version-scoped + a global "last bust timestamp" — survives a partial
  // network outage that left an inconsistent state.
  try {
    localStorage.setItem(CACHE_BUST_FLAG_KEY, '1');
    localStorage.setItem('ff_last_cache_bust_at', String(Date.now()));
    localStorage.setItem('ff_last_cache_bust_version', String(targetVersion || APP_VERSION));
  } catch { /* ignore quota */ }

  // Brief toast so the cashier knows what's happening — better than a
  // surprise reload.
  try {
    const el = document.createElement('div');
    el.style.cssText =
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);' +
      'background:#7c3aed;color:white;padding:16px 24px;border-radius:12px;' +
      'font:600 14px system-ui,sans-serif;box-shadow:0 8px 30px rgba(0,0,0,.3);' +
      'z-index:2147483647;pointer-events:none;';
    el.textContent = `Updating FlavorFeed to v${targetVersion}…`;
    document.body.appendChild(el);
  } catch { /* ignore */ }

  // Reload via cache-busting query so the WebView can't reuse its disk cache.
  setTimeout(() => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('_v', String(targetVersion));
      u.searchParams.set('_t', String(Date.now()));
      window.location.replace(u.toString());
    } catch {
      window.location.reload();
    }
  }, 600);
}

async function checkServerVersion() {
  try {
    const response = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
    if (!response.ok) return;
    const data = await response.json();
    const serverVersion = data?.version;
    if (!serverVersion || serverVersion === APP_VERSION) return;

    console.log(`🆕 New version available: ${serverVersion} (current: ${APP_VERSION})`);

    // Only hard-bust if we haven't already done it for THIS server version
    // (idempotent; protects against reload loops if version.json briefly
    //  flaps during a deploy).
    const flagKey = `ff_cache_busted_for_v${serverVersion}`;
    if (!localStorage.getItem(flagKey)) {
      await bustCachesAndReload(serverVersion);
    } else {
      // Already busted — just let SW pick up the update normally.
      const registration = await navigator.serviceWorker?.getRegistration();
      registration?.update?.();
    }
  } catch (e) {
    console.log('Version check failed (offline?)');
  }
}

window.APP_VERSION = APP_VERSION;

} // end of else (non-prerender) block
